import React from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';

const TYPE_LABELS = {
  BOLETA: 'Boletas',
  FACTURA: 'Facturas',
  NOTA_CREDITO: 'Notas de crédito',
  NOTA_DEBITO: 'Notas de débito',
  RETENCION: 'Retenciones',
  SIN_CLASIFICAR: 'Sin clasificar',
};

const formatNumber = (value = 0) => Number(value).toLocaleString('es-PE');

const formatDifference = (value = 0) => {
  const number = Number(value);

  return `${number > 0 ? '+' : ''}${formatNumber(number)}`;
};

export const BillingReconciliationSummary = ({ reconciliation, loading }) => {
  const hasData = reconciliation.has('totalGenerated');

  if (!hasData && !loading) {
    return null;
  }

  const totalGenerated = reconciliation.get('totalGenerated', 0);
  const totalReceived = reconciliation.get('totalReceived', 0);
  const difference = reconciliation.get('difference', 0);

  const byProcessType = reconciliation.get('byProcessType', Immutable.Map());

  const types = byProcessType.entrySeq().toArray();

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-bold mb-1">
            Conciliación de comprobantes electrónicos
          </h4>

          <p className="text-muted mb-0">
            Comparación de comprobantes generados en FLIT y registrados en
            Facturador.
          </p>
        </div>

        {loading && <span className="text-primary">Consultando...</span>}
      </div>

      {hasData && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted mb-2">Generados en FLIT</div>

                  <h2 className="fw-bold mb-0">
                    {formatNumber(totalGenerated)}
                  </h2>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted mb-2">
                    Registrados en Facturador
                  </div>

                  <h2 className="fw-bold text-primary mb-0">
                    {formatNumber(totalReceived)}
                  </h2>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted mb-2">Diferencia</div>

                  <h2
                    className={`fw-bold mb-0 ${
                      difference === 0 ? 'text-success' : 'text-warning'
                    }`}
                  >
                    {formatDifference(difference)}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Detalle por tipo de comprobante</h5>

              <div className="table-responsive">
                <table className="table table-bordered table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Tipo de comprobante</th>
                      <th className="text-end">FLIT</th>
                      <th className="text-end">Facturador</th>
                      <th className="text-end">Diferencia</th>
                    </tr>
                  </thead>

                  <tbody>
                    {types.map(([type, detail]) => {
                      const generated = detail.get('generated', 0);
                      const received = detail.get('received', 0);
                      const diff = detail.get('difference', 0);

                      return (
                        <tr key={type}>
                          <td>{TYPE_LABELS[type] || type}</td>

                          <td className="text-end">
                            {formatNumber(generated)}
                          </td>

                          <td className="text-end">{formatNumber(received)}</td>

                          <td
                            className={`text-end fw-bold ${
                              diff === 0 ? 'text-success' : 'text-warning'
                            }`}
                          >
                            {formatDifference(diff)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot className="table-light fw-bold">
                    <tr>
                      <td>Total</td>

                      <td className="text-end">
                        {formatNumber(totalGenerated)}
                      </td>

                      <td className="text-end">
                        {formatNumber(totalReceived)}
                      </td>

                      <td className="text-end">
                        {formatDifference(difference)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <small className="text-muted d-block mt-2">
            Las diferencias representan cantidades de comprobantes por fecha y
            tipo. No necesariamente corresponden a documentos faltantes.
          </small>
        </>
      )}
    </div>
  );
};

BillingReconciliationSummary.propTypes = {
  reconciliation: PropTypes.instanceOf(Immutable.Map).isRequired,
  loading: PropTypes.bool,
};

BillingReconciliationSummary.defaultProps = {
  loading: false,
};

export default BillingReconciliationSummary;
